import Form, { ServerError } from '~/components/Form';
import SubmitButton from '~/components/Form/SubmitButton';
import { Button } from '~/components/ui/Button';
import InputField from '~/components/ui/Forms/InputField';
import Modal from '~/components/ui/Modal';
import { getLocalizedString } from '~/i18n/utils';
import ApplicationType from './ApplicationType';
import { ModalSizes } from '~/components/ui/Modal/Modal';
import {
  getTranslationKey,
  getZodCreateNewApplicationSchema,
  MultiApplicationTabs
} from './utils';
import { useIntl } from '@cookbook/solid-intl';
import { Localized } from '~/i18n';
import {
  ApiConfigData,
  ApiErrorInstance,
  ApplicationTypeEnum,
  createClientForEnvironment,
  isApi5xxResponseStatus,
  withHandleError
} from '~/api';
import { validator } from '@felte/validator-zod';
import { useToast } from '~/components/ui/Toast';
import { useNavigate } from '@solidjs/router';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils';
import { useAppData } from '~/components/AppData';
import { HttpStatusCode } from 'axios';
import { mergeProps, Show } from 'solid-js';
import Alert from '~/components/ui/Alert';

type AddNewApplicationFormProps = {
  showAddNewAppModal: boolean;
  onClose: () => void;
};

type AddNewApplicationFormData = {
  applicationName: string;
  applicationType: ApplicationTypeEnum;
};

export default function AddNewApplicationModal(
  props: AddNewApplicationFormProps
) {
  const merged = mergeProps({}, props);
  const intl = useIntl();
  const toaster = useToast();
  const navigate = useNavigate();
  const appData = useAppData();

  const handleAddNewApplicationClick = async (data) => {
    const { applicationName: name, applicationType: clientType } =
      data as AddNewApplicationFormData;

    return createClientForEnvironment({
      name,
      clientType
    });
  };

  const handleSuccessfulClientCreation = (data) => {
    const localData = { ...data } as ApiConfigData;

    toaster.toast().success({
      description: getLocalizedString(
        getTranslationKey('toast_message_application_created_successfully'),
        { intl, applicationName: localData.name }
      )
    });

    merged.onClose();

    navigate(
      getSlug.environmentById(
        appData.selectedEnvironment?.id,
        replacePathParams(
          EnvironmentPagePath.ENVIRONMENT_MULTI_APPLICATION_TABS,
          {
            applicationId: localData.keyId,
            tabType: MultiApplicationTabs.CONFIGURATION
          }
        ) as EnvironmentPagePath,
        true
      )
    );
  };

  const handleCreateNewApplicationError = withHandleError(({ error, intl }) => {
    const serverError = error as ApiErrorInstance;

    if (
      serverError.httpStatus === HttpStatusCode.BadGateway ||
      isApi5xxResponseStatus(serverError.httpStatus)
    ) {
      return getLocalizedString(
        getTranslationKey('application_creation_failed'),
        { intl }
      );
    }
  });

  return (
    <Modal
      open={merged.showAddNewAppModal}
      size={ModalSizes.LARGE}
      title={getLocalizedString(getTranslationKey('create_application'))}
    >
      <Form
        initialValues={{ applicationName: '', applicationType: undefined }}
        extend={validator({ schema: getZodCreateNewApplicationSchema(intl) })}
        onSubmit={handleAddNewApplicationClick}
        onSuccess={handleSuccessfulClientCreation}
        errorHandler={handleCreateNewApplicationError}
      >
        {({ reset }) => {
          return (
            <div class="flex flex-col gap-5">
              <ServerError>
                {({ message }) => (
                  <Show when={message}>
                    <Alert variant="error">{message}</Alert>
                  </Show>
                )}
              </ServerError>
              <InputField
                name="applicationName"
                label={getLocalizedString(
                  getTranslationKey('application_name_input_label')
                )}
                placeholder={getLocalizedString(
                  'pages.multi_applications.application_name_input_placeholder'
                )}
                data-testid="create-application-modal-name-input"
              />
              <ApplicationType />
              <div class="flex gap-1 justify-end">
                <Button
                  onClick={() => {
                    reset();
                    merged.onClose();
                  }}
                  variant="outline"
                  size="sm"
                  data-testid="create-application-modal-cancel-button"
                >
                  <Localized translationKey={getTranslationKey('cta_cancel')} />
                </Button>
                <SubmitButton
                  size="sm"
                  type="submit"
                  data-testid="create-application-modal-cta"
                >
                  <Localized translationKey={getTranslationKey('cta_create')} />
                </SubmitButton>
              </div>
            </div>
          );
        }}
      </Form>
    </Modal>
  );
}
